// CB:BEGIN admin-pm-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../../lib/getApiUrl';
import { Building2, RefreshCw, Loader2, ChevronRight, CheckCircle2, AlertTriangle, Trash2, UserCheck } from 'lucide-react';
import { useImpersonation } from '../../context/ImpersonationContext';

const BRAND = '#123c3b';

interface AgreementStatus {
  status: 'ACTIVE' | 'OUTDATED' | 'UNSIGNED';
  acceptedAt: string | null;
  acceptedVersion: string | null;
  currentVersion: string;
}

interface PMRow {
  id: string;
  email: string;
  createdAt: string;
  propertyManagerProfile?: {
    id: string;
    companyName?: string | null;
    contactName?: string | null;
    phone?: string | null;
    portfolioSize?: number | null;
    serviceMarkets?: string | null;
    notes?: string | null;
    organizationId?: string | null;
  } | null;
  _pmCounts: { projectCount: number; propertyCount: number };
  _agreementStatus?: AgreementStatus;
}

interface PMDetail {
  pm: { id: string; email: string; createdAt: string; propertyManagerProfile?: any };
  properties: Array<{ id: string; label?: string | null; address1: string; city: string; state: string; zip: string; propertyType: string; _count: { projects: number } }>;
  projects: Array<{ id: string; address: string; city?: string; state?: string; projectType: string; status: string; priority: string; createdAt: string; bids: any[] }>;
  agreementInfo?: AgreementStatus;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  POSTED:          { bg: '#dbeafe', color: '#1d4ed8' },
  BIDS_COLLECTING: { bg: '#fef9c3', color: '#854d0e' },
  BIDS_READY:      { bg: '#dcfce7', color: '#166534' },
  DECIDING:        { bg: '#fef9c3', color: '#854d0e' },
  IN_PROGRESS:     { bg: '#e0f2fe', color: '#0369a1' },
  COMPLETED:       { bg: '#dcfce7', color: '#166534' },
  CANCELLED:       { bg: '#fee2e2', color: '#dc2626' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
  EMERGENCY: { bg: '#fee2e2', color: '#dc2626' },
  HIGH:      { bg: '#fef3c7', color: '#b45309' },
  NORMAL:    { bg: '#f1f5f9', color: '#475569' },
};

function fmtDate(d?: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
}

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, background: bg, color }}>{label}</span>
  );
}

interface PMFunnel {
  totalPMs: number;
  missingAgreement: number;
  noProperties: number;
  noProjects: number;
  pendingAck: number;
  awaitingBids: number;
  needsAttention: number;
  fullyActive: number;
}

const AdminPropertyManagersPage: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  const { getToken } = useAuth();
  const { enterSession, isImpersonating, impersonatedUser } = useImpersonation();
  const [pms, setPms] = useState<PMRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPm, setSelectedPm] = useState<PMDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notesEdit, setNotesEdit] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [funnel, setFunnel] = useState<PMFunnel | null>(null);
  const [enteringSession, setEnteringSession] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editFields, setEditFields] = useState({ companyName: '', contactName: '', phone: '', portfolioSize: '', serviceMarkets: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getToken();
    const base = getApiBaseUrl().replace(/\/$/, '');
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers ?? {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, data: json };
  }, [getToken]);

  const fetchPMs = useCallback(async () => {
    setLoading(true);
    try {
      const [pmRes, funnelRes] = await Promise.all([
        apiCall('/api/admin/property-managers'),
        apiCall('/api/admin/property-managers/funnel'),
      ]);
      if (pmRes.ok) setPms(pmRes.data.propertyManagers || []);
      if (funnelRes.ok) setFunnel(funnelRes.data);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const openDetail = useCallback(async (userId: string) => {
    setDetailLoading(true);
    try {
      const { ok, data } = await apiCall(`/api/admin/property-managers/${userId}`);
      if (ok) {
        setSelectedPm(data);
        setNotesEdit(data.pm?.propertyManagerProfile?.notes || '');
      }
    } finally {
      setDetailLoading(false);
    }
  }, [apiCall]);

  const saveNotes = async () => {
    if (!selectedPm) return;
    setNotesSaving(true);
    const { ok } = await apiCall(`/api/admin/property-managers/${selectedPm.pm.id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes: notesEdit }),
    });
    setNotesSaving(false);
    if (ok) {
      showToast('Notes saved.');
      fetchPMs();
    } else {
      showToast('Failed to save notes.', false);
    }
  };

  const deletePM = async (userId: string, email: string, fromDetail = false) => {
    const msg = `Permanently delete "${email}" and ALL their properties, projects, and organization data? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    const { ok, data } = await apiCall(`/api/admin/users/${userId}`, { method: 'DELETE' });
    if (ok) {
      showToast('Property manager deleted.');
      if (fromDetail) setSelectedPm(null);
      fetchPMs();
    } else {
      showToast(data?.error || 'Failed to delete property manager.', false);
    }
  };

  const startEditProfile = () => {
    const prof = selectedPm?.pm?.propertyManagerProfile;
    setEditFields({
      companyName: prof?.companyName || '',
      contactName: prof?.contactName || '',
      phone: prof?.phone || '',
      portfolioSize: prof?.portfolioSize != null ? String(prof.portfolioSize) : '',
      serviceMarkets: prof?.serviceMarkets || '',
    });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    if (!selectedPm) return;
    setProfileSaving(true);
    const { ok, data } = await apiCall(`/api/admin/property-managers/${selectedPm.pm.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        companyName: editFields.companyName || null,
        contactName: editFields.contactName || null,
        phone: editFields.phone || null,
        portfolioSize: editFields.portfolioSize !== '' ? Number(editFields.portfolioSize) : null,
        serviceMarkets: editFields.serviceMarkets || null,
      }),
    });
    setProfileSaving(false);
    if (ok) {
      showToast('Profile updated.');
      setEditingProfile(false);
      await openDetail(selectedPm.pm.id);
      fetchPMs();
    } else {
      showToast(data?.error || 'Failed to update profile.', false);
    }
  };

  const enterPmSession = async (userId: string) => {
    setEnteringSession(true);
    try {
      await enterSession(userId);
      onNavigate?.('pm-dashboard');
    } catch (e: any) {
      showToast(e?.message || 'Failed to enter session.', false);
    } finally {
      setEnteringSession(false);
    }
  };

  useEffect(() => { fetchPMs(); }, [fetchPMs]);

  if (selectedPm) {
    const p = selectedPm.pm;
    const prof = p.propertyManagerProfile;
    return (
      <div style={{ fontFamily: 'Inter, system-ui, sans-serif', padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
        {toast && (
          <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 10, background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b', fontWeight: 600, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
            {toast.msg}
          </div>
        )}
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <button onClick={() => setSelectedPm(null)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
              ← Back to Property Managers
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              {isImpersonating && impersonatedUser?.id === p.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 13, fontWeight: 600 }}>
                  <UserCheck size={14} /> Session Active
                </div>
              ) : (
                <button
                  onClick={() => enterPmSession(p.id)}
                  disabled={enteringSession}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: BRAND, border: 'none', borderRadius: 8, color: '#fff', cursor: enteringSession ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: enteringSession ? 0.7 : 1 }}
                >
                  <UserCheck size={14} /> {enteringSession ? 'Opening...' : 'Enter PM Session'}
                </button>
              )}
              <button
                onClick={() => deletePM(p.id, p.email, true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', border: '1.5px solid #fecaca', borderRadius: 8, color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                <Trash2 size={14} /> Delete PM
              </button>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, marginBottom: 4 }}>
                  {prof?.companyName || p.email}
                </h1>
                <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>{p.email} · Joined {fmtDate(p.createdAt)}</p>
              </div>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <button
                  onClick={startEditProfile}
                  style={{ padding: '7px 14px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, color: '#475569', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  Edit Profile
                </button>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: BRAND }}>{selectedPm.properties.length}</div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Properties</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: BRAND }}>{selectedPm.projects.length}</div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Projects</div>
                </div>
              </div>
            </div>

            {editingProfile ? (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {([
                    { key: 'companyName', label: 'Company Name' },
                    { key: 'contactName', label: 'Primary Contact' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'portfolioSize', label: 'Portfolio Size (units)', type: 'number' },
                    { key: 'serviceMarkets', label: 'Service Markets' },
                  ] as { key: keyof typeof editFields; label: string; type?: string }[]).map(({ key, label, type }) => (
                    <div key={key}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
                      <input
                        type={type || 'text'}
                        value={editFields[key]}
                        onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={saveProfile} disabled={profileSaving} style={{ padding: '8px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: profileSaving ? 0.7 : 1 }}>
                    {profileSaving ? 'Saving…' : 'Save Profile'}
                  </button>
                  <button onClick={() => setEditingProfile(false)} style={{ padding: '8px 16px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#64748b' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
                {prof?.contactName && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Primary Contact</div>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>{prof.contactName}</div>
                  </div>
                )}
                {prof?.phone && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Phone</div>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>{prof.phone}</div>
                  </div>
                )}
                {prof?.portfolioSize != null && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Portfolio Size</div>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>{prof.portfolioSize} units</div>
                  </div>
                )}
                {prof?.serviceMarkets && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Markets</div>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>{prof.serviceMarkets}</div>
                  </div>
                )}
              </div>
            )}

            {/* Agreement Status */}
            <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Platform Agreement</div>
              {(() => {
                const ag = selectedPm.agreementInfo;
                if (!ag || ag.status === 'UNSIGNED') {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
                      <AlertTriangle size={18} color="#b45309" />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>Not Signed</div>
                        <div style={{ fontSize: 12, color: '#b45309' }}>PM has not accepted the Platform Agreement (v{ag?.currentVersion ?? '1'})</div>
                      </div>
                    </div>
                  );
                }
                if (ag.status === 'OUTDATED') {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
                      <AlertTriangle size={18} color="#dc2626" />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>Agreement Outdated</div>
                        <div style={{ fontSize: 12, color: '#dc2626' }}>Signed v{ag.acceptedVersion} on {fmtDate(ag.acceptedAt ?? undefined)} — current is v{ag.currentVersion}</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                    <CheckCircle2 size={18} color="#22c55e" />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>Agreement Active — v{ag.acceptedVersion}</div>
                      <div style={{ fontSize: 12, color: '#15803d' }}>Signed on {fmtDate(ag.acceptedAt ?? undefined)}</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* CB:BEGIN pm-activation-milestones */}
            <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activation Milestones</div>
              {(() => {
                const ag = selectedPm.agreementInfo;
                const hasAgreement = ag?.status === 'ACTIVE';
                const hasProperty = selectedPm.properties.length > 0;
                const hasProject = selectedPm.projects.length > 0;
                const hasLiveProject = selectedPm.projects.some(pr => !['DRAFT', 'CANCELLED'].includes(pr.status));
                const hasBid = selectedPm.projects.some(pr => (pr.bids?.length ?? 0) > 0);
                const draftProjects = selectedPm.projects.filter(pr => pr.status === 'DRAFT').length;
                const milestones = [
                  { label: 'Account Created', done: true },
                  { label: 'Platform Agreement Signed', done: hasAgreement, detail: ag?.acceptedAt ? `Signed ${fmtDate(ag.acceptedAt)}` : null },
                  { label: 'First Property Added', done: hasProperty, detail: hasProperty ? `${selectedPm.properties.length} properties` : null },
                  { label: 'First Project Created', done: hasProject, detail: hasProject ? `${selectedPm.projects.length} projects` : null },
                  { label: 'First Project Acknowledged & Live', done: hasLiveProject, detail: draftProjects > 0 ? `${draftProjects} pending signature` : null },
                  { label: 'First Bid Received', done: hasBid },
                ];
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {milestones.map(({ label, done, detail }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: done ? '#dcfce7' : '#f1f5f9', border: `2px solid ${done ? '#22c55e' : '#e2e8f0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {done && <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 800 }}>✓</span>}
                        </div>
                        <div>
                          <span style={{ fontSize: 13, color: done ? '#0f172a' : '#94a3b8', fontWeight: done ? 600 : 400 }}>{label}</span>
                          {detail && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>{detail}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            {/* CB:END pm-activation-milestones */}

            <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Admin Notes</div>
              <textarea
                value={notesEdit}
                onChange={(e) => setNotesEdit(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="Internal notes about this PM account…"
              />
              <button onClick={saveNotes} disabled={notesSaving} style={{ marginTop: 8, padding: '8px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                {notesSaving ? 'Saving…' : 'Save Notes'}
              </button>
            </div>
          </div>

          {/* Properties */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24, marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Properties ({selectedPm.properties.length})</h2>
            {selectedPm.properties.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 14 }}>No properties added yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Label', 'Address', 'Type', 'Projects'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPm.properties.map(prop => (
                      <tr key={prop.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{prop.label || '—'}</td>
                        <td style={{ padding: '12px 14px', fontSize: 14, color: '#475569' }}>{prop.address1}, {prop.city}, {prop.state} {prop.zip}</td>
                        <td style={{ padding: '12px 14px', fontSize: 13, color: '#64748b' }}>{prop.propertyType}</td>
                        <td style={{ padding: '12px 14px', fontSize: 14 }}>{prop._count.projects}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Projects */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Projects ({selectedPm.projects.length})</h2>
            {selectedPm.projects.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 14 }}>No projects created yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Address', 'Type', 'Status', 'Priority', 'Bids', 'Created', 'Action'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPm.projects.map(proj => {
                      const sc = STATUS_COLORS[proj.status] ?? { bg: '#f1f5f9', color: '#475569' };
                      const pc = PRIORITY_COLORS[proj.priority] ?? PRIORITY_COLORS.NORMAL;
                      return (
                        <tr key={proj.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 14px', fontSize: 14, color: '#0f172a' }}>{proj.address}{proj.city ? `, ${proj.city}` : ''}</td>
                          <td style={{ padding: '12px 14px', fontSize: 13, color: '#64748b' }}>{proj.projectType}</td>
                          <td style={{ padding: '12px 14px' }}><Badge label={proj.status.replace(/_/g, ' ')} bg={sc.bg} color={sc.color} /></td>
                          <td style={{ padding: '12px 14px' }}><Badge label={proj.priority} bg={pc.bg} color={pc.color} /></td>
                          <td style={{ padding: '12px 14px', fontSize: 14 }}>{proj.bids?.length ?? 0}</td>
                          <td style={{ padding: '12px 14px', fontSize: 13, color: '#64748b' }}>{fmtDate(proj.createdAt)}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <button
                              onClick={() => onNavigate?.(`admin-project-edit-${proj.id}`)}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                            >
                              View <ChevronRight size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 10, background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b', fontWeight: 600, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
          {toast.msg}
        </div>
      )}
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Building2 size={28} style={{ color: BRAND }} />
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 }}>Property Managers</h1>
              <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>{pms.length} account{pms.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={fetchPMs} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* CB:BEGIN pm-admin-funnel-panel */}
        {funnel && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Total PMs', value: funnel.totalPMs, bg: '#f1f5f9', color: '#0f172a' },
              { label: 'Missing Agreement', value: funnel.missingAgreement, bg: funnel.missingAgreement > 0 ? '#fef3c7' : '#f1f5f9', color: funnel.missingAgreement > 0 ? '#92400e' : '#64748b' },
              { label: 'No Properties', value: funnel.noProperties, bg: funnel.noProperties > 0 ? '#fef3c7' : '#f1f5f9', color: funnel.noProperties > 0 ? '#92400e' : '#64748b' },
              { label: 'No Projects', value: funnel.noProjects, bg: funnel.noProjects > 0 ? '#fef3c7' : '#f1f5f9', color: funnel.noProjects > 0 ? '#92400e' : '#64748b' },
              { label: 'Pending Signature', value: funnel.pendingAck, bg: funnel.pendingAck > 0 ? '#fef3c7' : '#f1f5f9', color: funnel.pendingAck > 0 ? '#92400e' : '#64748b' },
              { label: 'Awaiting Bids', value: funnel.awaitingBids, bg: '#dbeafe', color: '#1d4ed8' },
              { label: 'Needs Attention', value: funnel.needsAttention, bg: funnel.needsAttention > 0 ? '#fee2e2' : '#f1f5f9', color: funnel.needsAttention > 0 ? '#dc2626' : '#64748b' },
              { label: 'Fully Active', value: funnel.fullyActive, bg: '#dcfce7', color: '#166534' },
            ].map(({ label, value, bg, color }) => (
              <div key={label} style={{ background: bg, borderRadius: 12, padding: '14px 18px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        )}
        {/* CB:END pm-admin-funnel-panel */}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: BRAND }} /></div>
        ) : pms.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 48, textAlign: 'center' }}>
            <Building2 size={40} style={{ color: '#cbd5e1', margin: '0 auto 16px' }} />
            <p style={{ color: '#64748b', fontSize: 16, margin: 0 }}>No property manager accounts yet.</p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Company / Email', 'Contact', 'Markets', 'Properties', 'Projects', 'Agreement', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pms.map(pm => {
                  const prof = pm.propertyManagerProfile;
                  const ag = pm._agreementStatus;
                  return (
                    <tr key={pm.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{prof?.companyName || '(no company name)'}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{pm.email}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: '#475569' }}>{prof?.contactName || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748b', maxWidth: 160 }}>{prof?.serviceMarkets || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: 15, fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>{pm._pmCounts.propertyCount}</td>
                      <td style={{ padding: '14px 16px', fontSize: 15, fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>{pm._pmCounts.projectCount}</td>
                      <td style={{ padding: '14px 16px' }}>
                        {!ag || ag.status === 'UNSIGNED'
                          ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={14} color="#f59e0b" /><span style={{ fontSize: 13, color: '#92400e' }}>Unsigned</span></span>
                          : ag.status === 'OUTDATED'
                          ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={14} color="#ef4444" /><span style={{ fontSize: 13, color: '#991b1b' }}>Outdated v{ag.acceptedVersion}</span></span>
                          : <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={14} color="#22c55e" /><span style={{ fontSize: 13, color: '#166534' }}>Signed v{ag.acceptedVersion}</span></span>
                        }
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(pm.createdAt)}</td>
                      {/* CB:BEGIN admin-pm-list-no-row-delete — View only; delete removed from list row */}
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          onClick={() => openDetail(pm.id)}
                          disabled={detailLoading}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          View <ChevronRight size={13} />
                        </button>
                      </td>
                      {/* CB:END admin-pm-list-no-row-delete */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPropertyManagersPage;
// CB:END admin-pm-page
